import React, { Fragment, useCallback, useEffect, useState } from "react";
import { map, pluck, sum } from "ramda";
import Grid from "@mui/material/Grid";
import { Box, Card } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { useCspSubmissionContentCount } from "../cspServices";
import { useDispatch } from "react-redux";
import { logOut } from "../../auth/authActions";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { notBlank } from "../../util/ext";
import DateRangeSelect, {
  calculateDate,
  dateRanges,
} from "../../components/dashboard/DateRangeSelect";

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 350,
    minHeight: 610,
    maxWidth: 350,
    maxHeight: 610,
    width: "100%",
    height: "100%",
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderRadius: 20,
    color: theme.palette.secondary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  body1: {
    fontSize: "1.2rem",
    textTransform: "uppercase",
  },

  body2: {
    fontSize: "1.4rem",
    lineHeight: "3.4rem",
    color: theme.palette.secondary.contrastText,
  },

  rangeSelect: {
    minWidth: 95,
  },

  graph: {
    // width: "calc(100% + 75px)",
    width: "100%",
    height: "100%",
    marginTop: theme.spacing(11),
  },

  progressWrapper: {
    flexGrow: 1,
  },
}));

const graphColors = {
  ALBUM: { color: "#03d3fc", label: "Albums" },
  AUDIO: { color: "#EFCBA1", label: "Audio" },
  AUDIOBOOK: { color: "#FF0000", label: "Audiobook" },
  BOOK: { color: "#D6EBD5", label: "Books" },
  DISSERTATION: { color: "#00FF00", label: "Dissertations" },
  IMAGE: { color: "#C9AAD5", label: "Images" },
  OTHER_DOCUMENT: { color: "#0000FF", label: "Documents" },
  VIDEO: { color: "#AADAE0", label: "Videos" },
  NO_DATA: { color: "#CCCCCC" },
};

const CspSubmissionsCard = () => {
  const classes = useStyles();
  const reduxDispatch = useDispatch();
  const [totalCount, setTotalCount] = useState(0);
  const [graphData, setGraphData] = useState([]);

  const [fromDate, setFromDate] = useState(calculateDate(dateRanges.last_7));
  const typeCountResults = useCspSubmissionContentCount(fromDate);

  useEffect(() => {
    if (typeCountResults.isError) {
      //-- Send user to login if the token has expired or not logged in
      if (typeCountResults.error.status === 401) {
        reduxDispatch(logOut());
      }
    }

    if (typeCountResults.isSuccess) {
      setGraphData(typeCountResults.data);
      const total = sum(pluck("count", typeCountResults.data));
      setTotalCount(total);
    } else {
      setGraphData([]);
      setTotalCount(0);
    }
  }, [
    typeCountResults.isError,
    typeCountResults.isSuccess,
    typeCountResults.error,
    typeCountResults.data,
    reduxDispatch,
  ]);

  const handleDateRangeChange = useCallback(value => {
    console.log(`Search Date : ${value}`);
    setFromDate(value);
  }, []);

  return (
    <Card classes={{ root: classes.root }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" component="span">
            CSP Submissions
          </Typography>
        </Grid>

        <Grid item>
          <Grid container alignItems="center" spacing={5}>
            <Grid item>
              <DateRangeSelect
                initialRange={dateRanges.last_7}
                onDateRangeChange={handleDateRangeChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!typeCountResults.isLoading ? (
        <Grid container spacing={5} justifyContent="flex-start">
          <Grid item>
            <Box style={{ marginRight: 50 }}>
              <Typography
                variant="body1"
                component="div"
                color="primary"
                classes={{ body1: classes.body1 }}
              >
                Total
              </Typography>
              <Typography
                variant="h1"
                component="div"
                classes={{ h1: classes.h1 }}
              >
                {totalCount.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box>
              <Typography
                variant="body1"
                component="div"
                classes={{ body1: classes.body1 }}
              >
                Pending
              </Typography>
              <Typography
                variant="h1"
                component="div"
                classes={{ h1: classes.h1 }}
              >
                {Number(0).toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Typography
            variant="h1"
            component="span"
            classes={{ h1: classes.h1 }}
          >
            Loading...
          </Typography>
        </Box>
      )}
      {!typeCountResults.isLoading ? (
        <Fragment>
          <Box mt={8} mb={8} ml={"auto"} mr={"auto"} height={270} width={270}>
            <ResponsiveContainer height={"100%"} width={"100%"}>
              <PieChart>
                <Pie
                  blendStroke
                  data={notBlank(graphData) ? graphData : [{ count: 1 }]}
                  cx={"50%"}
                  cy={"50%"}
                  innerRadius={100}
                  outerRadius={135}
                  paddingAngle={0}
                  dataKey="count"
                >
                  {notBlank(graphData) ? (
                    graphData.map((entry, index) => {
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={graphColors[entry.contentType].color}
                        />
                      );
                    })
                  ) : (
                    <Cell key={`cell-0`} fill={graphColors["NO_DATA"].color} />
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box>
            <Grid container spacing={3}>
              {map(
                type => (
                  <Grid key={type.contentType} item>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <Box
                          bgcolor={graphColors[type.contentType].color}
                          height={24}
                          width={24}
                          borderRadius="5px"
                        ></Box>
                      </Grid>
                      <Grid item>
                        <Typography
                          component="span"
                          variant="body2"
                          classes={{ body2: classes.body2 }}
                        >
                          {graphColors[type.contentType].label}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ),
                graphData,
              )}
            </Grid>
          </Box>
        </Fragment>
      ) : (
        <Box className={classes.progressWrapper}>
          <PanelLoadingIndicator />
        </Box>
      )}
    </Card>
  );
};

export default CspSubmissionsCard;
